import React, { useContext, useEffect, useMemo, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import ChatIcon from "@material-ui/icons/Chat";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import MoneyOffIcon from "@material-ui/icons/MoneyOff";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import WorkIcon from "@material-ui/icons/Work";
import WarningIcon from "@material-ui/icons/Warning";
import ShowChartIcon from "@material-ui/icons/ShowChart";

import {
	ResponsiveContainer,
	XAxis,
	YAxis,
	CartesianGrid,
	LineChart,
	Line,
	Legend,
	Tooltip as RcTooltip,
	PieChart,
	Pie,
	Cell
} from "recharts";

import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import DeltaKpi from "../../components/KpiCard";
import NextActions from "../../components/NextActions";
import { categorical, marks, chartTheme } from "../../theme/charts";
import { i18n } from "../../translate/i18n";

import Chart from "./Chart";

const useStyles = makeStyles(theme => ({
	greeting: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		flexWrap: "wrap",
		marginBottom: theme.spacing(2)
	},
	greetingTitle: { color: theme.palette.text.primary },
	greetingSub: { color: theme.palette.text.secondary },
	periodBox: { display: "flex", gap: theme.spacing(1), flexShrink: 0 },
	container: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(4)
	},
	kpiPaper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		minHeight: 96,
		borderLeft: "4px solid",
		borderLeftColor: theme.palette.primary.main
	},
	kpiPaperSuccess: { borderLeftColor: theme.palette.success?.main || "#4caf50" },
	kpiPaperWarn: { borderLeftColor: theme.palette.warning?.main || "#ff9800" },
	kpiPaperError: { borderLeftColor: theme.palette.error?.main || "#f44336" },
	kpiPaperInfo: { borderLeftColor: theme.palette.info?.main || "#2196f3" },
	kpiLabel: {
		fontSize: 12,
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
		letterSpacing: 0.5
	},
	kpiValue: {
		fontSize: 26,
		fontWeight: 600,
		lineHeight: 1.1
	},
	kpiSub: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: 2
	},
	kpiIcon: {
		fontSize: 36,
		opacity: 0.55
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1)
	},
	panel: {
		padding: theme.spacing(2),
		height: "100%"
	},
	chartBox: {
		width: "100%",
		height: 260
	},
	goalRow: {
		marginBottom: theme.spacing(1.5)
	},
	goalProgress: {
		height: 8,
		borderRadius: 4,
		marginTop: 4
	},
	listItemDense: {
		paddingTop: 4,
		paddingBottom: 4
	}
}));

const currencyBR = value =>
	new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
		maximumFractionDigits: 2
	}).format(Number(value || 0));

const compactBR = value =>
	new Intl.NumberFormat("pt-BR", {
		notation: "compact",
		maximumFractionDigits: 1
	}).format(Number(value || 0));

const dateBR = iso => {
	if (!iso) return "-";
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return iso;
		return d.toLocaleDateString("pt-BR");
	} catch (e) {
		return iso;
	}
};

// Paleta validada em theme/charts. A anterior reprovava em quatro checagens —
// entre elas verde x vermelho com ΔE 4,2 em deuteranopia, ou seja "ganho" e
// "perdido" indistinguíveis para ~8% dos homens.
const PIE_COLORS = categorical;

const KpiCard = ({ label, value, sub, icon: Icon, variant, classes }) => {
	const variantClass =
		variant === "success"
			? classes.kpiPaperSuccess
			: variant === "warn"
			? classes.kpiPaperWarn
			: variant === "error"
			? classes.kpiPaperError
			: variant === "info"
			? classes.kpiPaperInfo
			: "";
	return (
		<Paper className={`${classes.kpiPaper} ${variantClass}`}>
			<Box>
				<div className={classes.kpiLabel}>{label}</div>
				<div className={classes.kpiValue}>{value}</div>
				{sub ? <div className={classes.kpiSub}>{sub}</div> : null}
			</Box>
			{Icon ? <Icon className={classes.kpiIcon} color="action" /> : null}
		</Paper>
	);
};

const Dashboard = () => {
	const classes = useStyles();
	const muiTheme = useTheme();
	const chartTokens = useMemo(() => chartTheme(muiTheme), [muiTheme]);
	const { user } = useContext(AuthContext);

	// Recorte dos indicadores. O mês corrente é o padrão porque é a pergunta
	// que se faz todo dia; o comparativo vem do mesmo tamanho de período antes.
	const [period, setPeriod] = useState(() => {
		const hoje = new Date();
		const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
		return {
			start: ini.toISOString().slice(0, 10),
			end: hoje.toISOString().slice(0, 10)
		};
	});

	const userQueueIds = useMemo(
		() => (user && user.queues && user.queues.length ? user.queues.map(q => q.id) : []),
		[user]
	);
	const queueIdsParam = useMemo(
		() => JSON.stringify(userQueueIds),
		[userQueueIds]
	);

	const { count: openCount } = useTickets({
		status: "open",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: queueIdsParam
	});
	const { count: pendingCount } = useTickets({
		status: "pending",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: queueIdsParam
	});
	const { count: closedCount } = useTickets({
		status: "closed",
		showAll: "true",
		withUnreadMessages: "false",
		queueIds: queueIdsParam
	});

	const today = useMemo(() => new Date().toISOString(), []);
	const { tickets: todaysTickets } = useTickets({ date: today });

	const [sales, setSales] = useState(null);
	const [finance, setFinance] = useState(null);
	const [financeAccounts, setFinanceAccounts] = useState(null);
	const [operations, setOperations] = useState(null);
	const [goals, setGoals] = useState([]);

	useEffect(() => {
		let alive = true;
		const safe = async promise => {
			try {
				const { data } = await promise;
				return data;
			} catch (err) {
				return null;
			}
		};
		const fetchAll = async () => {
			const [s, f, fa, o, g] = await Promise.all([
				safe(api.get("/dashboard/sales", { params: period })),
				safe(api.get("/dashboard/finance", { params: period })),
				safe(api.get("/finance/dashboard")),
				safe(api.get("/dashboard/operations", { params: period })),
				safe(api.get("/dashboard/goals"))
			]);
			if (!alive) return;
			setSales(s);
			setFinance(f);
			setFinanceAccounts(fa);
			setOperations(o);
			setGoals(Array.isArray(g) ? g : []);
		};
		fetchAll();
		const id = setInterval(fetchAll, 60000);
		return () => {
			alive = false;
			clearInterval(id);
		};
			// Refaz a carga quando o recorte muda — senão o seletor de período
		// mexeria na tela sem mexer nos números.
	}, [period]);

	// Séries para os minigráficos. O cartão só precisa de { value }; a data
	// fica de fora de propósito — sparkline não tem eixo para lê-la.
	const wonSeriesCount = useMemo(
		() => (sales?.series?.won || []).map(d => ({ value: Number(d.count) || 0 })),
		[sales]
	);
	const wonSeriesAmount = useMemo(
		() => (sales?.series?.won || []).map(d => ({ value: Number(d.amount) || 0 })),
		[sales]
	);

	const cashflowSeries = useMemo(() => {
		if (!finance || !finance.series) return [];
		const map = new Map();
		(finance.series.receivables || []).forEach(r => {
			const k = r.date;
			map.set(k, { date: k, entradas: Number(r.value || 0), saidas: 0 });
		});
		(finance.series.payables || []).forEach(p => {
			const k = p.date;
			const cur = map.get(k) || { date: k, entradas: 0, saidas: 0 };
			cur.saidas = Number(p.value || 0);
			map.set(k, cur);
		});
		return Array.from(map.values()).sort((a, b) =>
			a.date < b.date ? -1 : 1
		);
	}, [finance]);

	const pipelineData = useMemo(() => {
		if (!sales || !sales.byStage) return [];
		return sales.byStage.map((s, idx) => ({
			name: `Estágio ${s.stageId != null ? s.stageId : "-"}`,
			amount: Number(s.amount || 0),
			count: Number(s.count || 0),
			color: PIE_COLORS[idx % PIE_COLORS.length]
		}));
	}, [sales]);

	const ticketsTodayCount = (todaysTickets && todaysTickets.length) || 0;

	const primeiroNome = String(user?.name || "").trim().split(" ")[0];
	const saudacao = (() => {
		const h = new Date().getHours();
		if (h < 12) return "Bom dia";
		if (h < 18) return "Boa tarde";
		return "Boa noite";
	})();

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				{/* Saudação e período: dizem de quem é a tela e a que recorte os
				    números se referem. Sem isso, "18.000" não tem contexto. */}
				<div className={classes.greeting}>
					<div>
						<Typography variant="h2" component="h1" className={classes.greetingTitle}>
							{saudacao}
							{primeiroNome ? `, ${primeiroNome}` : ""}! 👋
						</Typography>
						<Typography variant="body2" className={classes.greetingSub}>
							Aqui está o resumo do seu negócio hoje.
						</Typography>
					</div>
					<div className={classes.periodBox}>
						<TextField
							type="date"
							label="De"
							size="small"
							InputLabelProps={{ shrink: true }}
							value={period.start}
							onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))}
						/>
						<TextField
							type="date"
							label="Até"
							size="small"
							InputLabelProps={{ shrink: true }}
							value={period.end}
							onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))}
						/>
					</div>
				</div>

				{/* Antes dos indicadores: número serve para entender o mês,
				    esta lista serve para decidir o que fazer agora. */}
				<Grid container spacing={2} style={{ marginBottom: 8 }}>
					<Grid item xs={12}>
						<NextActions />
					</Grid>
				</Grid>

				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<KpiCard
							classes={classes}
							label={i18n.t("dashboard.messages.inAttendance.title")}
							value={openCount}
							icon={ChatIcon}
							variant="info"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<KpiCard
							classes={classes}
							label={i18n.t("dashboard.messages.waiting.title")}
							value={pendingCount}
							icon={HourglassEmptyIcon}
							variant="warn"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<KpiCard
							classes={classes}
							label={i18n.t("dashboard.messages.closed.title")}
							value={closedCount}
							icon={DoneAllIcon}
							variant="success"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<KpiCard
							classes={classes}
							label="Tickets hoje"
							value={ticketsTodayCount}
							icon={ShowChartIcon}
							sub={
								operations && operations.tasks && operations.tasks.open != null
									? `${operations.tasks.open} tarefas abertas`
									: undefined
							}
						/>
					</Grid>

					{sales ? (
						<>
							<Grid item xs={12} sm={6} md={3}>
								{/* Sem comparação: é um saldo do agora, não um
								    acumulado do período — comparar com "antes"
								    não diria nada. */}
								<DeltaKpi
									label="Negócios abertos"
									value={(sales.counters && sales.counters.openDeals) || 0}
									icon={WorkIcon}
									hint={`Pipeline: ${currencyBR(
										sales.amounts && sales.amounts.openAmount
									)}`}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<DeltaKpi
									label="Negócios ganhos"
									value={(sales.counters && sales.counters.wonDeals) || 0}
									icon={EmojiEventsIcon}
									delta={sales.deltas?.wonDeals}
									series={wonSeriesCount}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<DeltaKpi
									label="Valor ganho"
									value={currencyBR(sales.amounts && sales.amounts.wonAmount)}
									icon={TrendingUpIcon}
									delta={sales.deltas?.wonAmount}
									series={wonSeriesAmount}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								{/* Perder mais é pior: a cor segue o significado,
								    não a direção da seta. */}
								<DeltaKpi
									label="Negócios perdidos"
									value={(sales.counters && sales.counters.lostDeals) || 0}
									icon={TrendingDownIcon}
									delta={sales.deltas?.lostDeals}
									invertColors
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<DeltaKpi
									label="Taxa de conversão"
									value={`${Number(sales.conversionPct || 0).toFixed(1)}%`}
									icon={TrendingUpIcon}
									delta={sales.deltas?.conversionPct}
								/>
							</Grid>
						</>
					) : null}

					{finance ? (
						<>
							<Grid item xs={12} sm={6} md={3}>
								<DeltaKpi
									label="Entradas"
									value={currencyBR(finance.inflow)}
									icon={AttachMoneyIcon}
									delta={finance.deltas?.inflow}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								{/* Gastar mais não é bom notícia: cor invertida. */}
								<DeltaKpi
									label="Saídas"
									value={currencyBR(finance.outflow)}
									icon={MoneyOffIcon}
									delta={finance.deltas?.outflow}
									invertColors
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<DeltaKpi
									label="Fluxo líquido"
									value={currencyBR(finance.netCashflow)}
									icon={AccountBalanceWalletIcon}
									delta={finance.deltas?.netCashflow}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<KpiCard
									classes={classes}
									label="A receber pendente"
									value={currencyBR(finance.pending && finance.pending.receivables)}
									sub={
										finance.pending && finance.pending.receivablesOverdue
											? `Atrasados: ${currencyBR(
													finance.pending.receivablesOverdue
											  )}`
											: undefined
									}
									icon={WarningIcon}
									variant={
										finance.pending && finance.pending.receivablesOverdue
											? "warn"
											: "info"
									}
								/>
							</Grid>
						</>
					) : null}

					<Grid item xs={12} md={8}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Tickets por hora (hoje) — total {ticketsTodayCount}
							</Typography>
							<div className={classes.chartBox}>
								<Chart />
							</div>
						</Paper>
					</Grid>

					<Grid item xs={12} md={4}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Pipeline de vendas
							</Typography>
							{pipelineData.length === 0 ? (
								<Typography variant="body2" color="textSecondary">
									Sem negócios abertos no momento.
								</Typography>
							) : (
								<div className={classes.chartBox}>
									<ResponsiveContainer>
										<PieChart>
											<Pie
												data={pipelineData}
												dataKey="amount"
												nameKey="name"
												innerRadius={45}
												outerRadius={85}
												paddingAngle={2}
											>
												{pipelineData.map((entry, index) => (
													<Cell key={index} fill={entry.color} />
												))}
											</Pie>
											<RcTooltip formatter={value => currencyBR(value)} />
											<Legend />
										</PieChart>
									</ResponsiveContainer>
								</div>
							)}
						</Paper>
					</Grid>

					<Grid item xs={12} md={8}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Fluxo de caixa do período
							</Typography>
							{cashflowSeries.length === 0 ? (
								<Typography variant="body2" color="textSecondary">
									Sem movimentações no período.
								</Typography>
							) : (
								<div className={classes.chartBox}>
									<ResponsiveContainer>
										<LineChart data={cashflowSeries}>
											<CartesianGrid
												strokeDasharray="3 3"
												vertical={false}
												stroke={chartTokens.grid}
											/>
											<XAxis
												dataKey="date"
												tick={chartTokens.axisTick}
												tickLine={false}
												axisLine={{ stroke: chartTokens.grid }}
											/>
											<YAxis
												tickFormatter={compactBR}
												tick={chartTokens.axisTick}
												tickLine={false}
												axisLine={false}
												width={56}
											/>
											<RcTooltip
												formatter={value => currencyBR(value)}
												contentStyle={chartTokens.tooltip}
												cursor={{ stroke: chartTokens.grid, strokeWidth: 1 }}
											/>
											<Legend iconType="plainline" />
											{/* Azul e laranja, e não verde e vermelho: o par
											    anterior tinha ΔE 4,2 em deuteranopia — duas
											    linhas que viravam a mesma cor para quem tem
											    daltonismo, justamente num gráfico onde uma é
											    dinheiro entrando e a outra saindo. */}
											<Line
												type="monotone"
												dataKey="entradas"
												name="Entradas"
												stroke={categorical[0]}
												strokeWidth={marks.lineWidth}
												dot={false}
												activeDot={{ r: marks.activeDotRadius }}
											/>
											<Line
												type="monotone"
												dataKey="saidas"
												name="Saídas"
												stroke={categorical[1]}
												strokeWidth={marks.lineWidth}
												dot={false}
												activeDot={{ r: marks.activeDotRadius }}
											/>
										</LineChart>
									</ResponsiveContainer>
								</div>
							)}
						</Paper>
					</Grid>

					<Grid item xs={12} md={4}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Metas
							</Typography>
							{goals.length === 0 ? (
								<Typography variant="body2" color="textSecondary">
									Nenhuma meta ativa.
								</Typography>
							) : (
								goals.slice(0, 6).map(g => (
									<Box key={g.id} className={classes.goalRow}>
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="center"
										>
											<Typography variant="body2" noWrap>
												{g.name}
											</Typography>
											<Chip
												size="small"
												label={`${Number(g.percent || 0).toFixed(0)}%`}
												color={
													Number(g.percent || 0) >= 100
														? "primary"
														: Number(g.percent || 0) >= 60
														? "default"
														: "secondary"
												}
											/>
										</Box>
										<LinearProgress
											className={classes.goalProgress}
											variant="determinate"
											value={Math.min(100, Number(g.percent || 0))}
										/>
										<Typography variant="caption" color="textSecondary">
											{currencyBR(g.currentValue)} de {currencyBR(g.targetValue)}
										</Typography>
									</Box>
								))
							)}
						</Paper>
					</Grid>

					<Grid item xs={12} md={6}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Próximos a receber (30 dias)
							</Typography>
							<Divider />
							{!(
								financeAccounts &&
								financeAccounts.upcomingReceivables &&
								financeAccounts.upcomingReceivables.length
							) ? (
								<Box mt={1}>
									<Typography variant="body2" color="textSecondary">
										Sem recebíveis previstos.
									</Typography>
								</Box>
							) : (
								<List dense>
									{financeAccounts.upcomingReceivables.slice(0, 6).map(item => (
										<ListItem
											key={item.id}
											className={classes.listItemDense}
											disableGutters
										>
											<ListItemText
												primary={item.description || `#${item.id}`}
												secondary={`Vence em ${dateBR(item.dueDate)}`}
											/>
											<Typography variant="body2" style={{ fontWeight: 600 }}>
												{currencyBR(item.amount)}
											</Typography>
										</ListItem>
									))}
								</List>
							)}
							{financeAccounts && financeAccounts.overdueReceivables ? (
								<Box mt={1}>
									<Tooltip title="Recebíveis vencidos não recebidos">
										<Chip
											size="small"
											icon={<WarningIcon />}
											label={`${financeAccounts.overdueReceivables} em atraso`}
											color="secondary"
										/>
									</Tooltip>
								</Box>
							) : null}
						</Paper>
					</Grid>

					<Grid item xs={12} md={6}>
						<Paper className={classes.panel}>
							<Typography
								variant="subtitle1"
								className={classes.sectionTitle}
								color="primary"
							>
								Próximos a pagar (30 dias)
							</Typography>
							<Divider />
							{!(
								financeAccounts &&
								financeAccounts.upcomingPayables &&
								financeAccounts.upcomingPayables.length
							) ? (
								<Box mt={1}>
									<Typography variant="body2" color="textSecondary">
										Sem contas a pagar previstas.
									</Typography>
								</Box>
							) : (
								<List dense>
									{financeAccounts.upcomingPayables.slice(0, 6).map(item => (
										<ListItem
											key={item.id}
											className={classes.listItemDense}
											disableGutters
										>
											<ListItemText
												primary={item.description || `#${item.id}`}
												secondary={`Vence em ${dateBR(item.dueDate)}`}
											/>
											<Typography variant="body2" style={{ fontWeight: 600 }}>
												{currencyBR(item.amount)}
											</Typography>
										</ListItem>
									))}
								</List>
							)}
							{financeAccounts && financeAccounts.overduePayables ? (
								<Box mt={1}>
									<Tooltip title="Contas a pagar vencidas">
										<Chip
											size="small"
											icon={<WarningIcon />}
											label={`${financeAccounts.overduePayables} em atraso`}
											color="secondary"
										/>
									</Tooltip>
								</Box>
							) : null}
						</Paper>
					</Grid>

					{operations ? (
						<Grid item xs={12}>
							<Paper className={classes.panel}>
								<Typography
									variant="subtitle1"
									className={classes.sectionTitle}
									color="primary"
								>
									Operações
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={6} sm={3}>
										<Typography variant="caption" color="textSecondary">
											Tickets abertos
										</Typography>
										<Typography variant="h6">
											{(operations.tickets && operations.tickets.open) || 0}
										</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="caption" color="textSecondary">
											Tickets finalizados (período)
										</Typography>
										<Typography variant="h6">
											{(operations.tickets && operations.tickets.closedInPeriod) || 0}
										</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="caption" color="textSecondary">
											Tarefas concluídas (período)
										</Typography>
										<Typography variant="h6">
											<AssignmentTurnedInIcon
												fontSize="small"
												style={{ verticalAlign: "middle", marginRight: 4 }}
											/>
											{(operations.tasks && operations.tasks.doneInPeriod) || 0}
										</Typography>
									</Grid>
									<Grid item xs={6} sm={3}>
										<Typography variant="caption" color="textSecondary">
											Projetos ativos
										</Typography>
										<Typography variant="h6">
											{(operations.projects && operations.projects.active) || 0}
										</Typography>
									</Grid>
								</Grid>
							</Paper>
						</Grid>
					) : null}
				</Grid>
			</Container>
		</div>
	);
};

export default Dashboard;
